import type { AppLocale } from '@/i18n/locales'
import type { BuffSpeechPreferences } from './speech-types'
import { MANBO_VOICE_ID } from './manbo'
export type BuffPresence = 'unknown' | 'present' | 'missing'
export type BuffRunState = 'idle' | 'preparing' | 'running' | 'waiting' | 'interrupted' | 'error'
export interface BuffReminderSettings {
  threshold: number
  visual: boolean
  speech: boolean
  nearing: boolean
}
export interface BuffManualTimerSettings { enabled: boolean; seconds: number }
export interface BuffDefinition {
  id: string
  customAudioId?: string
  customAudioEnabled?: boolean
  name: string
  image: Blob
  fingerprint: string
  order: number
  enabled: boolean
  sourceSkillId?: string
  sourceItemId?: string
  reminders: BuffReminderSettings
  manualTimer?: BuffManualTimerSettings
  updatedAt: number
}
export function usesCustomAudio(item?: BuffDefinition): boolean { return item?.customAudioEnabled ?? !!item?.customAudioId }
export function activeCustomAudioId(item?: BuffDefinition): string | undefined {
  // An empty ID still requests local playback, so an unconfigured local mode never generates speech remotely.
  return usesCustomAudio(item) ? item?.customAudioId ?? '' : undefined
}
export interface BuffLayoutSettings {
  mode: 'combined' | 'stats' | 'buffs'
  arrangement: 'auto' | 'vertical' | 'horizontal'
  reverse: boolean
  flow: 'wrap' | 'row' | 'column'
  iconSize: 24 | 32 | 40 | 48
  gap: number
  names: boolean
}
export interface BuffPreferences {
  reminderDefaultsVersion?: number
  speechDefaultsVersion?: number
  /** Legacy defaults, read only during migration. New targets use defaultBuffReminderSettings(). */
  reminders: BuffReminderSettings
  layout: BuffLayoutSettings
  speech: BuffSpeechPreferences
  region: { x: number; y: number; width: number; height: number } | null
}
export interface BuffObservation {
  /** Manual timers continue through evidence gaps and accept reliable game calibrations. */
  timerSource?: 'manual'
  /** Confirmed game-side digit hiding; recognition timing waits for a fresh sequence. */
  numberHidden?: boolean
  /** Icon carried from an earlier frame; its actual evidence timestamp never slides. */
  held?: boolean
  /** Computed from an established endpoint; never renews icon or digit evidence. */
  predicted?: boolean
  /** Absolute prediction deadline, bounded by the established estimated end. */
  predictionValidUntil?: number
  id: string
  presence: BuffPresence
  seconds: number | null
  upperSeconds: number | null
  capturedAt: number
  cycle: number
  reason: string
  timeReason?: string
  /** Absolute timestamps on the monitoring clock; omitted on legacy fixtures. */
  validUntil?: number
  numberValidUntil?: number
  numberEvidenceAt?: number
  iconEvidenceAt?: number
  endpoint?: [number, number]
  estimatedEndAt?: number
  box?: [number, number, number, number]
}
export interface BuffDisplayItem extends BuffObservation {
  /** Older preview snapshots omit this field; omitted means enabled. */
  enabled?: boolean
  name: string
  imageUrl: string
  nearing: boolean
  visual: boolean
}
export interface BuffSnapshot {
  state: BuffRunState
  items: BuffDisplayItem[]
  layout: BuffLayoutSettings
  now: number
  locale: AppLocale
  alerts: string[]
}
export interface BuffSkill {
  skillID: string
  skillName: string
  jobID: string
  jobName: string
  jobGroup: string
  icon: string
}
export interface BuffCatalog {
  ok: true
  items: BuffSkill[]
  meta: { page: number; pageSize: number; total: number; totalPages: number }
  jobs: { id: string; name: string; count: number }[]
}
export interface BuffItem {
  itemID: string
  itemName: string
  category: string
  description: string
  icon: string
}
export interface BuffItemCatalog {
  ok: true
  items: BuffItem[]
  meta: BuffCatalog['meta']
}
export type BuffSource = { kind: 'skill' | 'item'; id: string }
// Full-width upper HUD band, excluding the bottom action bar.
export function defaultBuffRegion(): NonNullable<BuffPreferences['region']> {
  return { x: 0, y: 0, width: 1, height: .3 }
}
export function defaultBuffReminderSettings(): BuffReminderSettings {
  return { threshold: 10, visual: true, speech: true, nearing: true }
}
export function validManualTimerSeconds(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 86400
}
export function manualTimerSettings(definition: Pick<BuffDefinition, 'manualTimer'>): BuffManualTimerSettings {
  const saved = definition.manualTimer
  return { enabled: saved?.enabled === true, seconds: saved && validManualTimerSeconds(saved.seconds) ? saved.seconds : 120 }
}
export function defaultBuffPreferences(): BuffPreferences {
  return {
    reminderDefaultsVersion: 6,
    speechDefaultsVersion: 1,
    reminders: defaultBuffReminderSettings(),
    layout: { mode: 'combined', arrangement: 'auto', reverse: false, flow: 'wrap', iconSize: 32, gap: 8, names: false },
    speech: { voices: { 'zh-CN': MANBO_VOICE_ID }, volume: 1, rate: 1.5 },
    region: defaultBuffRegion(),
  }
}
export function enabledBuffCount(snapshot: BuffSnapshot | null | undefined): number {
  return snapshot?.items.filter(item => item.enabled !== false).length ?? 0
}
export function reminderSettings(definition: BuffDefinition, _preferences?: BuffPreferences): BuffReminderSettings {
  const defaults = defaultBuffReminderSettings()
  return {
    threshold: definition.reminders?.threshold ?? defaults.threshold,
    visual: true,
    speech: definition.reminders?.speech ?? defaults.speech,
    nearing: definition.reminders?.nearing ?? defaults.nearing,
  }
}
export function visibleBuffSeconds(item: BuffObservation, now: number): number | null {
  if (item.numberHidden && item.timerSource !== 'manual') return null
  const remaining = remainingBuffSeconds(item, now)
  return remaining === null ? null : Math.floor(remaining)
}
/** Continuous internal time, independent of digit visibility and display rounding. */
export function remainingBuffSeconds(item: BuffObservation, now: number): number | null {
  if (!buffCountdownFresh(item, now)) return null
  const remaining = item.estimatedEndAt !== undefined ? (item.estimatedEndAt - now) / 1000 : item.seconds! - Math.max(0, now - item.capturedAt) / 1_000
  return remaining > 0 ? remaining : null
}
export function observationFresh(item: BuffObservation, now: number): boolean {
  if (item.timerSource === 'manual') return now >= item.capturedAt && now < (item.estimatedEndAt ?? -Infinity)
  if (item.predicted && item.held && item.iconEvidenceAt !== item.capturedAt
    && item.predictionValidUntil !== undefined && now >= item.predictionValidUntil) return false
  return now >= item.capturedAt && now <= (item.validUntil ?? item.capturedAt + 2000)
}
/** Frame freshness and countdown validity are independent; legacy observations keep their leases. */
export function buffCountdownFresh(item: BuffObservation, now: number): boolean {
  if (item.presence !== 'present' || item.seconds === null || !observationFresh(item, now)) return false
  return item.predictionValidUntil === undefined
    ? now <= (item.numberValidUntil ?? Infinity)
    : now < item.predictionValidUntil && (item.estimatedEndAt === undefined || now < item.estimatedEndAt)
}
